/**
 * The button that folds the model library away, and remembers that it was folded.
 *
 * All the deciding lives in `domain/sidebar.ts`; this is the wiring — an attribute on the
 * root element, a value in `localStorage`, a media query and a click handler. The same
 * shape as `ThemeToggle`, and for the same reasons.
 *
 * The attribute goes on the root element rather than on the sidebar itself, because what
 * changes is the page's layout: the grid gives the column its width, and the column cannot
 * set its own. It is written only for a collapsed library, so the default state needs no
 * attribute and a page whose script has not run is a page with its library open.
 *
 * The media query is watched rather than merely read, because the shape can change under a
 * page that is already up: a phone turned on its side crosses the breakpoint, and so does a
 * desktop window dragged narrow. Nothing about the fold *state* changes when it does — that
 * is the user's choice and it stands — but the arrow on the button has to turn to face the
 * way the panel now moves.
 *
 * Nothing here resizes the canvas. A `ResizeObserver` on the canvas host already watches for
 * exactly this, since the window can change the same measurement without anybody pressing
 * anything, and one path is easier to be sure of than two.
 */

import {
  describeSidebarToggle,
  initialSidebarCollapsed,
  SIDEBAR_BAND_QUERY,
  SIDEBAR_STORAGE_KEY,
  type SidebarLayout,
  sidebarStorageValue,
} from '../domain/sidebar.js';

export interface SidebarToggleOptions {
  readonly button: HTMLButtonElement;
  /**
   * The library has been folded or unfolded.
   *
   * Not called for the starting state, unlike the theme's equivalent, and deliberately so.
   * This has to be set up before the first frame — it is what decides how wide the canvas
   * starts out — which is before the parts that care about the answer exist. So the caller
   * reads `collapsed` once when it is ready, and hears about changes after that.
   */
  readonly onChange: (collapsed: boolean) => void;
}

export class SidebarToggle {
  private readonly button: HTMLButtonElement;
  private folded: boolean;
  private layout: SidebarLayout;

  constructor(private readonly options: SidebarToggleOptions) {
    this.button = options.button;

    const band = window.matchMedia(SIDEBAR_BAND_QUERY);
    this.layout = band.matches ? 'band' : 'column';
    this.folded = initialSidebarCollapsed(read(), this.layout);

    band.addEventListener('change', (event) => {
      this.layout = event.matches ? 'band' : 'column';

      // Relabelled, not re-decided. Turning a phone sideways is not an instruction to
      // unfold anything, and a panel that opened itself on every rotation would be worse
      // than one whose arrow briefly pointed the old way.
      this.apply();
    });

    this.button.addEventListener('click', () => {
      this.set(!this.folded);
      this.options.onChange(this.folded);
    });

    this.apply();
  }

  /** Whether the library is folded away right now. */
  get collapsed(): boolean {
    return this.folded;
  }

  private set(collapsed: boolean): void {
    this.folded = collapsed;

    // A browser refusing to store — private browsing, or storage that is full — is not a
    // reason to leave the button unable to fold the library for this session.
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarStorageValue(collapsed));
    } catch (error) {
      console.warn('The sidebar state could not be remembered.', error);
    }

    this.apply();
  }

  private apply(): void {
    const root = document.documentElement;

    if (this.collapsed) root.dataset.sidebar = 'collapsed';
    else delete root.dataset.sidebar;

    const { text, description } = describeSidebarToggle(this.collapsed, this.layout);
    this.button.textContent = text;
    this.button.title = description;
    this.button.setAttribute('aria-label', description);

    // Describes the library, not the button: `aria-controls` points at the panel, and this
    // says whether that panel is showing.
    this.button.setAttribute('aria-expanded', String(!this.collapsed));
  }
}

function read(): string | null {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
  } catch {
    // Same reasoning as writing: an unreadable store means no remembered choice, which is
    // a state this already handles.
    return null;
  }
}
